import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  viewsList,
  viewsCreate,
  viewsUpdate,
  viewsDelete,
  viewsSetDefault,
  viewsClearDefault,
} from "../gen";
import type {
  ConversationViewResponse,
  ConversationViewCreateRequest,
  ConversationViewUpdateRequest,
} from "../gen/models";
import { useRole } from "../hooks/useRole";
import { useToast } from "./ToastContext";

export interface ColorOption {
  id: string;
  name: string;
  borderClass: string;
  bgClass: string;
  hex: string;
}

export const COLOR_OPTIONS: ColorOption[] = [
  { id: "emerald", name: "Emerald", borderClass: "border-emerald-500", bgClass: "bg-emerald-500", hex: "#10b981" },
  { id: "indigo", name: "Indigo", borderClass: "border-indigo-500", bgClass: "bg-indigo-500", hex: "#6366f1" },
  { id: "amber", name: "Amber", borderClass: "border-amber-500", bgClass: "bg-amber-500", hex: "#f59e0b" },
  { id: "rose", name: "Rose", borderClass: "border-rose-500", bgClass: "bg-rose-500", hex: "#f43f5e" },
  { id: "sky", name: "Sky", borderClass: "border-sky-500", bgClass: "bg-sky-500", hex: "#0ea5e9" },
  { id: "purple", name: "Purple", borderClass: "border-purple-500", bgClass: "bg-purple-500", hex: "#a855f7" },
  { id: "orange", name: "Orange", borderClass: "border-orange-500", bgClass: "bg-orange-500", hex: "#f97316" },
  { id: "teal", name: "Teal", borderClass: "border-teal-500", bgClass: "bg-teal-500", hex: "#14b8a6" },
  { id: "stone", name: "Stone", borderClass: "border-stone-500", bgClass: "bg-stone-500", hex: "#78716c" },
];

export function getColorOption(colorId?: string): ColorOption {
  return COLOR_OPTIONS.find((c) => c.id === colorId) || COLOR_OPTIONS[0];
}

interface ConversationViewsContextType {
  views: ConversationViewResponse[];
  loading: boolean;
  error: string | null;
  activeViewId: string | null;
  activeView: ConversationViewResponse | null;
  defaultView: ConversationViewResponse | null;
  refreshViews: () => Promise<void>;
  createView: (payload: ConversationViewCreateRequest) => Promise<ConversationViewResponse>;
  updateView: (viewId: string, payload: ConversationViewUpdateRequest) => Promise<ConversationViewResponse>;
  deleteView: (viewId: string) => Promise<void>;
  setDefaultView: (viewId: string) => Promise<void>;
  clearDefaultView: (viewId: string) => Promise<void>;
  toggleDefaultView: (viewId: string) => Promise<void>;
  applyView: (view: ConversationViewResponse) => void;
  clearActiveView: () => void;
}

const ConversationViewsContext = createContext<ConversationViewsContextType | undefined>(undefined);

export function ConversationViewsProvider({ children }: { children: React.ReactNode }) {
  const { orgId } = useRole();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [views, setViews] = useState<ConversationViewResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const activeViewId = searchParams.get("view_id");
  const activeView = views.find((v) => v.id === activeViewId) || null;

  const refreshViews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await viewsList();
      const data = (res as any).data;
      if (Array.isArray(data)) {
        setViews(data);
      } else {
        setViews([]);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load views");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshViews();
  }, [orgId, refreshViews]);

  const createView = async (payload: ConversationViewCreateRequest): Promise<ConversationViewResponse> => {
    try {
      const res = await viewsCreate(payload);
      const newView = (res as any).data as ConversationViewResponse;
      setViews((prev) => [newView, ...prev.filter((v) => v.id !== newView.id)]);
      showToast(`View "${newView.name}" created`, { type: "success" });
      return newView;
    } catch (err: any) {
      showToast(err?.message || "Failed to create view", { type: "error" });
      throw err;
    }
  };

  const updateView = async (
    viewId: string,
    payload: ConversationViewUpdateRequest
  ): Promise<ConversationViewResponse> => {
    try {
      const res = await viewsUpdate(viewId, payload);
      const updated = (res as any).data as ConversationViewResponse;
      setViews((prev) => prev.map((v) => (v.id === viewId ? updated : v)));
      showToast(`View "${updated.name}" updated`, { type: "success" });
      return updated;
    } catch (err: any) {
      showToast(err?.message || "Failed to update view", { type: "error" });
      throw err;
    }
  };

  const deleteView = async (viewId: string): Promise<void> => {
    try {
      await viewsDelete(viewId);
      setViews((prev) => prev.filter((v) => v.id !== viewId));
      if (activeViewId === viewId) {
        clearActiveView();
      }
      showToast("View deleted", { type: "success" });
    } catch (err: any) {
      showToast(err?.message || "Failed to delete view", { type: "error" });
      throw err;
    }
  };

  const defaultView = views.find((v) => v.is_default) || null;

  const setDefaultView = async (viewId: string): Promise<void> => {
    try {
      await viewsSetDefault(viewId);
      setViews((prev) =>
        prev.map((v) => ({
          ...v,
          is_default: v.id === viewId,
        }))
      );
      const target = views.find((v) => v.id === viewId);
      showToast(`Set "${target?.name || "View"}" as your default view`, { type: "success" });
    } catch (err: any) {
      showToast(err?.message || "Failed to set default view", { type: "error" });
      throw err;
    }
  };

  const clearDefaultView = async (viewId: string): Promise<void> => {
    try {
      await viewsClearDefault(viewId);
      setViews((prev) =>
        prev.map((v) => (v.id === viewId ? { ...v, is_default: false } : v))
      );
      showToast("Cleared default view", { type: "success" });
    } catch (err: any) {
      showToast(err?.message || "Failed to clear default view", { type: "error" });
      throw err;
    }
  };

  const toggleDefaultView = async (viewId: string): Promise<void> => {
    const target = views.find((v) => v.id === viewId);
    if (target?.is_default) {
      await clearDefaultView(viewId);
    } else {
      await setDefaultView(viewId);
    }
  };

  const applyView = (view: ConversationViewResponse) => {
    const params = new URLSearchParams();
    params.set("view_id", view.id);

    const f = (view.filters || {}) as Record<string, any>;
    if (f.agent && f.agent !== "all") {
      params.set("agent", String(f.agent));
    }
    if (f.status && f.status !== "all") {
      params.set("status", String(f.status));
    }
    if (f.quick_filter && f.quick_filter !== "all") {
      params.set("quick_filter", String(f.quick_filter));
    } else if (f.evaluation && f.evaluation !== "all") {
      params.set("quick_filter", String(f.evaluation));
    }
    if (f.q && String(f.q).trim()) {
      params.set("q", String(f.q).trim());
    }

    navigate(`/threads?${params.toString()}`);
  };

  const clearActiveView = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("view_id");
      return next;
    }, { replace: true });
  };

  return (
    <ConversationViewsContext.Provider
      value={{
        views,
        loading,
        error,
        activeViewId,
        activeView,
        defaultView,
        refreshViews,
        createView,
        updateView,
        deleteView,
        setDefaultView,
        clearDefaultView,
        toggleDefaultView,
        applyView,
        clearActiveView,
      }}
    >
      {children}
    </ConversationViewsContext.Provider>
  );
}

export function useConversationViews() {
  const context = useContext(ConversationViewsContext);
  if (!context) {
    throw new Error("useConversationViews must be used within a ConversationViewsProvider");
  }
  return context;
}
